import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, AutomationInterface, WorkflowStepInterface } from '@vendasta/automata';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { SalesOpportunitiesService } from '../../../../data-services/sales-opportunities.service';
import { ActivityTableEntry, AutomationDisplayService, NodeData } from '../../common/automation-display.service';

@Injectable({ providedIn: 'root' })
export class ChangeOpportunityPipelineStageService extends AutomationDisplayService {
  constructor(
    protected readonly translateService: TranslateService,
    protected readonly salesOpportunitiesService: SalesOpportunitiesService,
  ) {
    super();
  }

  /** Gets text for the activity column in the table. */
  getActivityColumn(
    namespace: string,
    activity: ActivityInterface,
    automation: AutomationInterface,
  ): ActivityTableEntry {
    return this.tableEntry(
      this.translateService.stream(
        'AUTOMATIONS.EDITOR.TASKS.CHANGE_OPPORTUNITY_PIPELINE_STAGE.CHANGE_OPPORTUNITY_PIPELINE_STAGE',
      ),
      this.getDefaultStepColumnBody(namespace, activity.workflowStep, automation),
    );
  }

  /** The text in an automation node (i.e. the nodes in the graph). */
  getAutomationNodeData(
    _namespace: string,
    step: WorkflowStepInterface,
    _isWaitUntilNode: boolean,
    _automation?: AutomationInterface,
  ): Observable<NodeData> {
    const workflowStepData = step?.data ? JSON.parse(step.data) : {}; //pipeline_id and stage are the 2 keys
    return this.salesOpportunitiesService.pipelines$.pipe(
      take(1),
      switchMap((pipelines) => {
        const pipeline = pipelines.find((p) => p.pipelineId === workflowStepData.pipeline_id);
        if (!pipeline) {
          return this.nodeDisplayData(
            this.translateService.stream(
              'AUTOMATIONS.EDITOR.TASKS.CHANGE_OPPORTUNITY_PIPELINE_STAGE.CHANGE_OPPORTUNITY_PIPELINE_STAGE',
            ),
            this.validationErrorsDetected(
              step,
              this.translateService.instant(
                'AUTOMATIONS.EDITOR.TASKS.CHANGE_OPPORTUNITY_PIPELINE_STAGE.PIPELINE_NOT_FOUND',
              ),
              true,
            ),
          );
        }
        const stage = pipeline?.stages.find((s) => (s.probability || 0) === workflowStepData.stage);
        if (!stage && workflowStepData.stage !== 0) {
          return this.nodeDisplayData(
            this.translateService.stream(
              'AUTOMATIONS.EDITOR.TASKS.CHANGE_OPPORTUNITY_PIPELINE_STAGE.CHANGE_OPPORTUNITY_PIPELINE_STAGE',
            ),
            this.validationErrorsDetected(
              step,
              this.translateService.instant(
                'AUTOMATIONS.EDITOR.TASKS.CHANGE_OPPORTUNITY_PIPELINE_STAGE.STAGE_NOT_FOUND',
              ),
              true,
            ),
          );
        }
        return this.nodeDisplayData(
          this.translateService.stream('AUTOMATIONS.EDITOR.TASKS.CHANGE_OPPORTUNITY_PIPELINE_STAGE.ACTIVITY', {
            pipeline: pipeline?.name,
            stage: stage?.name,
          }),
          this.noValidationErrorsDetected(),
        );
      }),
    );
  }
}
